import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { Article } from '../../../utils/articleApi';
import { formatArticleCategoriesLine } from '../../../utils/articleCategories';
import { getArticleStylesCSS } from '../../../styles/articleContentStyles';
import { stripArticleBoilerplate } from '../../../utils/stripArticleBoilerplate';
import { splitBodyAtMidCta } from '../../../utils/articleMidCta';
import {
  extractMarkdownHeadings,
  prepareArticleHtml,
  stripEmbeddedMarkdownToc,
  type TocEntry,
} from '../../../utils/articleToc';
import ArticleTableOfContents from '../../../components/article/ArticleTableOfContents';
import AppDownloadButtons from '../../../components/feature/AppDownloadButtons';

interface ArticleContentProps {
  article: Article;
}

interface PreparedArticleBody {
  content: string;
  toc: TocEntry[];
  isHtml: boolean;
}

function prepareArticleBody(raw: string): PreparedArticleBody {
  const trimmed = raw.trim();
  const isHtml = trimmed.startsWith('<');

  if (isHtml) {
    const { html, toc } = prepareArticleHtml(raw);
    return { content: html, toc, isHtml: true };
  }

  const markdown = stripEmbeddedMarkdownToc(raw);
  return {
    content: markdown,
    toc: extractMarkdownHeadings(markdown),
    isHtml: false,
  };
}

function createMarkdownHeadingComponents(toc: TocEntry[]): Components {
  let h2Index = 0;
  let h3Index = 0;

  return {
    h2: ({ children }) => {
      const entry = toc.filter((item) => item.level === 2)[h2Index];
      h2Index += 1;
      return <h2 id={entry?.id}>{children}</h2>;
    },
    h3: ({ children }) => {
      const entry = toc.filter((item) => item.level === 3)[h3Index];
      h3Index += 1;
      return <h3 id={entry?.id}>{children}</h3>;
    },
  };
}

function renderArticleBodyBlock(body: string, prepared: PreparedArticleBody) {
  const trimmed = body.trim();
  if (!trimmed) {
    return null;
  }

  if (prepared.isHtml) {
    return <div dangerouslySetInnerHTML={{ __html: body }} />;
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={createMarkdownHeadingComponents(prepared.toc)}>
      {body}
    </ReactMarkdown>
  );
}

export default function ArticleContent({ article }: ArticleContentProps) {
  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="text-center">
          <p className="text-gray-600 text-sm sm:text-base">旅行記事データを読み込んでいます...</p>
        </div>
      </div>
    );
  }

  const { prepared, before, after, hasMid } = useMemo(() => {
    const bodyContent = stripArticleBoilerplate(article.content || '');
    const full = prepareArticleBody(bodyContent);
    const split = splitBodyAtMidCta(full.content);
    return {
      prepared: full,
      before: split.before,
      after: split.after,
      hasMid: split.hasMid,
    };
  }, [article.content]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <style>{getArticleStylesCSS()}</style>

      <div className="mb-6 sm:mb-8">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-4">
          <span>{article.publishDate}</span>
          <span>•</span>
          <span>{formatArticleCategoriesLine(article)}</span>
          <span>•</span>
          <span>{article.readTime}</span>
        </div>

        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">{article.title}</h1>

        <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8">{article.excerpt}</p>

        <div className="mb-6 sm:mb-8">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-48 sm:h-64 md:h-96 object-cover rounded-lg"
          />
        </div>
      </div>

      <div className="article-content">
        <ArticleTableOfContents entries={prepared.toc} />
        {renderArticleBodyBlock(before, prepared)}
        {hasMid && (
          <>
            <AppDownloadButtons articleId={article.id} placement="article_mid" />
            {renderArticleBodyBlock(after, prepared)}
          </>
        )}
      </div>

      <AppDownloadButtons articleId={article.id} placement="article_body" />

      <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200">
        <div className="flex items-center gap-3 sm:gap-4">
          <img
            src="/apple-touch-icon.png"
            alt="SOCIAL TRIP"
            width={48}
            height={48}
            className="h-10 w-10 shrink-0 rounded-full border border-gray-200 bg-white object-cover sm:h-12 sm:w-12"
          />
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm sm:text-base">{article.author}</p>
            <p className="text-xs sm:text-sm text-gray-600">投稿日: {article.publishDate}</p>
          </div>
        </div>

        <div className="mt-3 sm:mt-4">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
